import { FC, useId } from 'react';
import { UtilityText } from 'components/Typography';
import { HvpTag } from 'screens/Summary/HvpTag';
import { RestrictedRecipientStatus } from 'store/recipients/types';
import { CompetitivePrimaryDetail } from './CompetitivePrimaryCandidate/CompetitivePrimaryDetail';
import { RestrictedCandidateDetail } from './RestrictedCandidate/RestrictedCandidateDetail';

interface Props {
  competitiveness?: string;
  electionGoal?: string;
  financialsUpdatedAt?: string;
  fundraisingTarget: string;
  impactScore?: string;
  raised: string;
  primaryElectionDate?: string;
  isCompetitivePrimary?: boolean;
  restrictedStatus?: RestrictedRecipientStatus;
  hideImpactScore?: boolean;
  variant?: 'search' | 'page';
}

export const Details: FC<Props> = ({
  competitiveness,
  electionGoal,
  impactScore,
  primaryElectionDate,
  isCompetitivePrimary,
  restrictedStatus,
  hideImpactScore,
  variant = 'page',
}) => {
  const tooltipId = useId();
  const competitivenessTooltipId = `${tooltipId}-competitiveness`;
  const seatStatusTooltipId = `${tooltipId}-seat-status`;
  const hasDetailRows =
    impactScore ||
    isCompetitivePrimary ||
    restrictedStatus ||
    (variant === 'search' && (competitiveness || electionGoal));

  if (!hasDetailRows) {
    return hideImpactScore ? null : (
      <div className="no-impact-score">
        <UtilityText>
          Impact Score not available for this recipient. Oath's Impact Score does not currently
          score Organizations or hyper local races where sufficient data is not available.
        </UtilityText>
      </div>
    );
  }

  return (
    <div className="candidate-details-column">
      {competitiveness && (
        <>
          <span
            className="detail-tooltip-wrapper"
            tabIndex={0}
            aria-describedby={competitivenessTooltipId}
          >
            <UtilityText>Competitiveness:</UtilityText>
            <span className="detail-tooltip-text" role="tooltip" id={competitivenessTooltipId}>
              Oath uses polling, expert ratings, historic trends and other election forecasting to
              rate how competitive each race is likely to be.
            </span>
          </span>
          <HvpTag text={competitiveness} />
        </>
      )}
      {electionGoal && (
        <>
          <span
            className="detail-tooltip-wrapper"
            tabIndex={0}
            aria-describedby={seatStatusTooltipId}
          >
            <UtilityText>Seat Status:</UtilityText>
            <span className="detail-tooltip-text" role="tooltip" id={seatStatusTooltipId}>
              Hold indicates a Democratic-held seat that must be retained to win back control of the
              chamber. Flip indicates a Republican-held seat that must be captured to win back
              control of the chamber.
            </span>
          </span>
          <HvpTag text={electionGoal[0].toUpperCase() + electionGoal.toLowerCase().slice(1)} />
        </>
      )}
      {/* Temporarily hide Financial Need bar: OATH-3324 */}
      {/* {!isNaN(amountRaised) && (
        <FinancialNeed amountRaised={amountRaised} financialsUpdatedAt={financialsUpdatedAt} />
      )} */}
      {isCompetitivePrimary && (
        <CompetitivePrimaryDetail primaryElectionDate={primaryElectionDate} />
      )}
      {restrictedStatus && <RestrictedCandidateDetail recipientStatus={restrictedStatus} />}
    </div>
  );
};
